import { z } from 'zod'
import { toast } from 'sonner'
import { format } from 'date-fns'
import { th } from 'date-fns/locale'
import { paths } from '@/routes/paths'
import { useForm } from 'react-hook-form'
import { useState, useEffect } from 'react'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { fError } from '@/utils/format-error'
import axios, { endpoints } from '@/lib/axios'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { useBoolean } from '@/hooks/use-boolean'
import { Calendar } from '@/components/ui/calendar'
import { CalendarIcon, X, Plus } from 'lucide-react'
import { useRouter } from '@/routes/hooks/use-router'
import { zodResolver } from '@hookform/resolvers/zod'
import RichTextEditor from '@/components/ui/rich-text-editor'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  MultipleMediaUpload,
  type FileUpload,
} from '@/components/share-components/multiple-media-upload'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import {
  type ImagesWithHeader,
  uploadImagesWithHeader,
  deleteMarkedMediaFiles,
} from '@/utils/upload-media-helper'

import { type Privilege, PrivilegeType } from './privilege.types'

// Main form schema
const privilegeFormSchema = z.object({
  name: z.string().min(1, 'กรุณากรอกชื่อของรางวัล'),
  type: z.nativeEnum(PrivilegeType),
  description: z.string().optional(),
  isEnable: z.boolean(),
  startDate: z.date(),
  endDate: z.date(),
  expiredDate: z.date().optional(),
  totalQuantity: z.number().min(1, 'กรุณากรอกจำนวนที่ถูกต้อง'),
  limitPerUser: z.number().min(1, 'กรุณากรอกจำนวนที่ถูกต้อง'),
  privilegeCodes: z.array(z.string()).optional(),
})

type FormData = z.infer<typeof privilegeFormSchema>

// ----------------------------------------------------------------------

interface PrivilegeFormProps {
  privilege?: Privilege
  mode: 'create' | 'edit'
}

export default function PrivilegeForm({ privilege, mode }: PrivilegeFormProps) {
  const router = useRouter()
  const submitting = useBoolean(false)
  const [deletedMediaUrls, setDeletedMediaUrls] = useState<string[]>([])
  const [newCode, setNewCode] = useState('')

  // Debug logging for date conversion
  console.log('Privilege data received:', {
    startDate: privilege?.startDate,
    startDateType: typeof privilege?.startDate,
    endDate: privilege?.endDate,
    endDateType: typeof privilege?.endDate,
    expiredDate: privilege?.expiredDate,
    expiredDateType: typeof privilege?.expiredDate,
  })
  const [images, setImages] = useState<ImagesWithHeader>({
    header: privilege?.imageHeaderUrl
      ? { url: privilege.imageHeaderUrl, fileName: 'header' }
      : null,
    gallery:
      privilege?.mediaUrls?.map((media) => ({
        url: media.url,
        fileName: media.name,
        type: media.type,
        size: media.size,
      })) || [],
  })

  const form = useForm<FormData>({
    resolver: zodResolver(privilegeFormSchema),
    defaultValues: {
      name: privilege?.name || '',
      type: privilege?.type || PrivilegeType.ITEM,
      description: privilege?.description || '',
      isEnable: privilege?.isEnable ?? false,
      startDate: privilege?.startDate
        ? typeof privilege.startDate === 'string'
          ? new Date(privilege.startDate)
          : privilege.startDate
        : new Date(),
      endDate: privilege?.endDate
        ? typeof privilege.endDate === 'string'
          ? new Date(privilege.endDate)
          : privilege.endDate
        : new Date(),
      expiredDate: privilege?.expiredDate
        ? typeof privilege.expiredDate === 'string'
          ? new Date(privilege.expiredDate)
          : privilege.expiredDate
        : undefined,
      totalQuantity: privilege?.totalQuantity || 1,
      limitPerUser: privilege?.limitPerUser || 1,
      privilegeCodes: privilege?.privilegeCodes?.map((code) => code.code) || [],
    },
  })

  const handleHeaderImageChange = (file: File | null) => {
    if (file) {
      setImages({
        ...images,
        header: {
          url: file,
          fileName: file.name,
        },
      })
    } else {
      setImages({
        ...images,
        header: null,
      })
    }
  }

  const handleGalleryImageChange = (files: FileUpload[]) => {
    setImages({
      ...images,
      gallery: files,
    })
  }

  const handleGalleryImageDelete = (deletedUrl: string) => {
    if (mode === 'edit') {
      // In edit mode, track deleted URLs for later cleanup
      setDeletedMediaUrls((prev) => [...prev, deletedUrl])
      console.log('Gallery image marked for deletion:', deletedUrl)
    } else {
      // In create mode, just log the deletion
      console.log('Gallery image deleted:', deletedUrl)
    }
  }

  const handleAddCode = () => {
    if (
      newCode.trim() &&
      !form.getValues('privilegeCodes')?.includes(newCode.trim())
    ) {
      const currentCodes = form.getValues('privilegeCodes') || []
      const newCodes = [...currentCodes, newCode.trim()]
      form.setValue('privilegeCodes', newCodes)

      // Update totalQuantity based on privilegeCodes length for COUPON type
      if (form.getValues('type') === PrivilegeType.COUPON) {
        form.setValue('totalQuantity', newCodes.length)
      }

      setNewCode('')
    }
  }

  const handleRemoveCode = (codeToRemove: string) => {
    const currentCodes = form.getValues('privilegeCodes') || []
    const newCodes = currentCodes.filter((code) => code !== codeToRemove)
    form.setValue('privilegeCodes', newCodes)

    // Update totalQuantity based on privilegeCodes length for COUPON type
    if (form.getValues('type') === PrivilegeType.COUPON) {
      form.setValue('totalQuantity', newCodes.length)
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleAddCode()
    }
  }

  // Watch for type changes and update totalQuantity for COUPON type
  const watchedType = form.watch('type')
  const watchedPrivilegeCodes = form.watch('privilegeCodes')

  useEffect(() => {
    if (watchedType === PrivilegeType.COUPON) {
      const codesLength = watchedPrivilegeCodes?.length || 0
      form.setValue('totalQuantity', codesLength)
    }
  }, [watchedType, watchedPrivilegeCodes, form])

  const onSubmit = async (data: FormData) => {
    // Prevent multiple submissions
    if (submitting.value) {
      console.log('Form submission blocked - already submitting')
      return
    }

    console.log('Starting form submission:', { mode, data, images })

    try {
      submitting.onTrue()

      // Upload images using helper function
      const uploadedImages = await uploadImagesWithHeader(images, 'privileges')

      // Prepare privilege data
      const privilegeData = {
        ...data,
        ...uploadedImages,
        privilegeCodes: data.privilegeCodes || [],
      }

      console.log('Prepared privilege data:', privilegeData)

      let privilegeId: string

      if (mode === 'create') {
        console.log('Creating new privilege...')
        const response = await axios.post(
          endpoints.privileges.create,
          privilegeData,
        )
        privilegeId = response.data.id
        console.log('Privilege created successfully with ID:', privilegeId)
      } else {
        console.log('Updating privilege:', privilegeData)
        await axios.patch(
          endpoints.privileges.update(privilege!.id),
          privilegeData,
        )
        privilegeId = privilege!.id
        console.log('Privilege updated successfully')
      }

      // Delete media files that were marked for deletion (only in edit mode)
      if (mode === 'edit' && deletedMediaUrls.length > 0) {
        await deleteMarkedMediaFiles(deletedMediaUrls)
      }

      toast.success(
        mode === 'create' ? 'เพิ่มของรางวัลสำเร็จ' : 'แก้ไขของรางวัลสำเร็จ',
      )

      console.log(
        'Form submission completed successfully, navigating to privileges list',
      )
      router.push(paths.privileges)
    } catch (error) {
      console.error('Form submission failed:', error)
      console.error('Error details:', {
        message: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined,
      })
      toast.error(fError(error))
    } finally {
      console.log(
        'Form submission process finished, resetting submitting state',
      )
      submitting.onFalse()
    }
  }

  const handleBack = () => {
    router.push(paths.privileges)
  }

  const renderHeaderImageUpload = () => {
    const image = images.header

    return (
      <div className="space-y-2">
        <Label className="text-sm font-medium text-black">รูปภาพหลัก</Label>
        <div className="space-y-2">
          <Input
            type="file"
            accept="image/*"
            onChange={(e) => {
              const file = e.target.files?.[0] || null
              handleHeaderImageChange(file)
            }}
            className="rounded-lg border"
            key={image ? 'has-image' : 'no-image'} // Force re-render to clear input
          />
          {image && (
            <div className="relative w-full max-w-80 rounded-lg overflow-hidden bg-gray-100 border aspect-[1.5/1]">
              {typeof image.url === 'string' ? (
                <img
                  src={image.url}
                  alt="รูปภาพหลัก"
                  className="w-full h-full object-cover"
                />
              ) : (
                <img
                  src={URL.createObjectURL(image.url)}
                  alt="รูปภาพหลัก"
                  className="w-full h-full object-cover"
                />
              )}
              <Button
                type="button"
                variant="destructive"
                size="sm"
                onClick={() => {
                  handleHeaderImageChange(null)
                  // Clear the file input value
                  const fileInput = document.querySelector(
                    'input[type="file"]',
                  ) as HTMLInputElement
                  if (fileInput) {
                    fileInput.value = ''
                  }
                }}
                className="absolute top-1 right-1 h-6 w-6 p-0"
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Form */}
      <Form {...form}>
        <form
          id="privilege-form"
          onSubmit={form.handleSubmit(onSubmit)}
          className="space-y-6"
        >
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Form */}
            <div className="lg:col-span-2 space-y-6">
              <Card className="border rounded-lg">
                <CardHeader>
                  <CardTitle className="text-base font-semibold text-black">
                    ข้อมูลหลัก
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Name */}
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-medium text-black">
                          ชื่อของรางวัล
                        </FormLabel>
                        <FormControl>
                          <Input
                            placeholder="กรอกชื่อของรางวัล"
                            className="rounded-lg border"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Type */}
                  <FormField
                    control={form.control}
                    name="type"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-medium text-black">
                          ประเภท
                        </FormLabel>
                        <Select
                          onValueChange={field.onChange}
                          defaultValue={field.value}
                        >
                          <FormControl>
                            <SelectTrigger className="rounded-lg border">
                              <SelectValue placeholder="เลือกประเภท" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value={PrivilegeType.ITEM}>
                              สินค้า
                            </SelectItem>
                            <SelectItem value={PrivilegeType.COUPON}>
                              คูปอง
                            </SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {form.watch('type') === PrivilegeType.COUPON && (
                    <FormField
                      control={form.control}
                      name="privilegeCodes"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-sm font-medium text-black">
                            รหัสคูปอง
                          </FormLabel>
                          <div className="space-y-3">
                            <div className="flex gap-2">
                              <Input
                                placeholder="กรอกรหัสคูปอง"
                                value={newCode}
                                onChange={(e) => setNewCode(e.target.value)}
                                onKeyPress={handleKeyPress}
                                className="rounded-lg border"
                              />
                              <Button
                                type="button"
                                onClick={handleAddCode}
                                disabled={!newCode.trim()}
                                size="sm"
                                className="px-3"
                              >
                                <Plus className="h-4 w-4" />
                              </Button>
                            </div>

                            {field.value && field.value.length > 0 && (
                              <div className="flex flex-wrap gap-2">
                                {field.value.map((code, index) => (
                                  <Badge
                                    key={index}
                                    variant="secondary"
                                    className="flex items-center gap-1 px-2 py-1"
                                  >
                                    {code}
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleRemoveCode(code)}
                                      className="h-4 w-4 p-0 hover:bg-destructive hover:text-destructive-foreground"
                                    >
                                      <X className="h-3 w-3" />
                                    </Button>
                                  </Badge>
                                ))}
                              </div>
                            )}

                            {(!field.value || field.value.length === 0) && (
                              <p className="text-sm text-muted-foreground">
                                ยังไม่มีรหัสคูปอง กรุณาเพิ่มรหัสคูปอง
                              </p>
                            )}
                          </div>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  )}

                  {/* Description */}
                  <FormField
                    control={form.control}
                    name="description"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-medium text-black">
                          คำอธิบาย
                        </FormLabel>
                        <FormControl>
                          <RichTextEditor
                            value={field.value || ''}
                            onChange={field.onChange}
                            placeholder="กรอกคำอธิบายของรางวัล"
                            className="min-h-[200px]"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Images */}
                  <div className="space-y-4">{renderHeaderImageUpload()}</div>
                </CardContent>
              </Card>

              {/* Gallery Images */}
              <MultipleMediaUpload
                title="รูปภาพเพิ่มเติม"
                uploadedFiles={images.gallery}
                onFilesChange={handleGalleryImageChange}
                onFileDelete={handleGalleryImageDelete}
                maxFiles={10}
                maxSize={50}
                acceptedTypes={['image/*']}
                columns={2}
              />
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Basic Info */}
              <Card className="border rounded-lg">
                <CardHeader>
                  <CardTitle className="text-base font-semibold text-black">
                    ข้อมูลพื้นฐาน
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Enable Status */}
                  <FormField
                    control={form.control}
                    name="isEnable"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
                        <div className="space-y-0.5">
                          <FormLabel className="text-sm font-medium text-black">
                            เปิดใช้งาน
                          </FormLabel>
                          <div className="text-xs text-muted-foreground">
                            เปิด/ปิดการใช้งานของรางวัลนี้
                          </div>
                        </div>
                        <FormControl>
                          <Switch
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />

                  {/* Start Date */}
                  <FormField
                    control={form.control}
                    name="startDate"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-medium text-black">
                          วันที่เริ่มต้น
                        </FormLabel>
                        <Popover>
                          <PopoverTrigger asChild>
                            <FormControl>
                              <Button
                                variant="outline"
                                className="w-full justify-start text-left font-normal rounded-lg border"
                              >
                                <CalendarIcon className="mr-2 h-4 w-4" />
                                {field.value ? (
                                  format(field.value, 'dd/MM/yyyy', {
                                    locale: th,
                                  })
                                ) : (
                                  <span>เลือกวันที่</span>
                                )}
                              </Button>
                            </FormControl>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start">
                            <Calendar
                              mode="single"
                              selected={field.value}
                              onSelect={field.onChange}
                            />
                          </PopoverContent>
                        </Popover>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* End Date */}
                  <FormField
                    control={form.control}
                    name="endDate"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-medium text-black">
                          วันที่สิ้นสุด
                        </FormLabel>
                        <Popover>
                          <PopoverTrigger asChild>
                            <FormControl>
                              <Button
                                variant="outline"
                                className="w-full justify-start text-left font-normal rounded-lg border"
                              >
                                <CalendarIcon className="mr-2 h-4 w-4" />
                                {field.value ? (
                                  format(field.value, 'dd/MM/yyyy', {
                                    locale: th,
                                  })
                                ) : (
                                  <span>เลือกวันที่</span>
                                )}
                              </Button>
                            </FormControl>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start">
                            <Calendar
                              mode="single"
                              selected={field.value}
                              onSelect={field.onChange}
                              initialFocus
                            />
                          </PopoverContent>
                        </Popover>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Total Quantity */}
                  <FormField
                    control={form.control}
                    name="totalQuantity"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-medium text-black">
                          จำนวนทั้งหมด
                          {watchedType === PrivilegeType.COUPON && (
                            <span className="text-xs text-muted-foreground ml-2">
                              (อัปเดตอัตโนมัติตามจำนวนรหัสคูปอง)
                            </span>
                          )}
                        </FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            placeholder="กรอกจำนวนทั้งหมด"
                            className="rounded-lg border"
                            min="1"
                            {...field}
                            onChange={(e) =>
                              field.onChange(Number(e.target.value))
                            }
                            readOnly={watchedType === PrivilegeType.COUPON}
                            style={{
                              backgroundColor:
                                watchedType === PrivilegeType.COUPON
                                  ? '#f5f5f5'
                                  : undefined,
                              cursor:
                                watchedType === PrivilegeType.COUPON
                                  ? 'not-allowed'
                                  : undefined,
                            }}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Limit Per User */}
                  <FormField
                    control={form.control}
                    name="limitPerUser"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-medium text-black">
                          จำกัดต่อผู้ใช้
                        </FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            placeholder="กรอกจำนวนจำกัดต่อผู้ใช้"
                            className="rounded-lg border"
                            min="1"
                            {...field}
                            onChange={(e) =>
                              field.onChange(Number(e.target.value))
                            }
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 py-6 border-t">
            <Button type="submit" disabled={submitting.value}>
              {submitting.value
                ? 'กำลังบันทึก...'
                : mode === 'create'
                  ? 'เพิ่มของรางวัล'
                  : 'บันทึกการแก้ไข'}
            </Button>
            <Button type="button" variant="outline" onClick={handleBack}>
              ยกเลิก
            </Button>
          </div>
        </form>
      </Form>
    </div>
  )
}
